// Anzeige-Helfer: Datum, Gruppen, Initialen, Farben — alles in Europe/Berlin.
import type { Address } from '@mail/shared';

const TZ = 'Europe/Berlin';
const dayKey = (d: Date) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);

export function fmtWhen(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  const heute = dayKey(now);
  const gestern = dayKey(new Date(now.getTime() - 864e5));
  const k = dayKey(d);
  if (k === heute)
    return d.toLocaleTimeString('de-DE', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });
  if (k === gestern) return 'Gestern';
  const tage = (now.getTime() - d.getTime()) / 864e5;
  if (tage < 6) return d.toLocaleDateString('de-DE', { timeZone: TZ, weekday: 'short' });
  if (d.getFullYear() === now.getFullYear())
    return d.toLocaleDateString('de-DE', { timeZone: TZ, day: 'numeric', month: 'short' });
  return d.toLocaleDateString('de-DE', {
    timeZone: TZ,
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const heute = dayKey(new Date());
  const t = d.toLocaleTimeString('de-DE', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });
  if (dayKey(d) === heute) return `Heute, ${t}`;
  if (dayKey(d) === dayKey(new Date(Date.now() - 864e5))) return `Gestern, ${t}`;
  return (
    d.toLocaleDateString('de-DE', {
      timeZone: TZ,
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }) +
    ', ' +
    t
  );
}

// Gruppen in der Liste: Heute / Gestern / Diese Woche / <Monat>
export function groupOf(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return 'Älter';
  const d = new Date(iso);
  const k = dayKey(d);
  if (k === dayKey(now)) return 'Heute';
  if (k === dayKey(new Date(now.getTime() - 864e5))) return 'Gestern';
  // Montag der laufenden Woche (Berlin)
  const wd = new Intl.DateTimeFormat('en-US', { timeZone: TZ, weekday: 'short' }).format(now);
  const idx = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(wd);
  const montag = dayKey(new Date(now.getTime() - idx * 864e5));
  if (k >= montag) return 'Diese Woche';
  return d.toLocaleDateString('de-DE', {
    timeZone: TZ,
    month: 'long',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
}

export function initials(a: Address | null | undefined): string {
  if (!a) return '?';
  const n = (a.name ?? '').trim();
  if (n) {
    const parts = n
      .replace(/["']/g, '')
      .split(/[\s,]+/)
      .filter(Boolean);
    if (parts.length >= 2) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
    return n.slice(0, 2).toUpperCase();
  }
  return a.address.slice(0, 2).toUpperCase();
}

const PALETTE = [
  '#7A6FD6',
  '#2E9E5B',
  '#C93AB4',
  '#D9822B',
  '#3B6FE0',
  '#B08A12',
  '#E04848',
  '#4F86F0',
  '#22B573',
  '#8A5CD6',
  '#0E9AA7',
  '#C9662E',
];
export function avatarColor(a: Address | null | undefined): string {
  const s = (a?.address ?? '?').toLowerCase();
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length]!;
}

export function displayName(a: Address | null | undefined): string {
  if (!a) return '';
  return a.name?.trim() || a.address;
}

// Beteiligte wie in Canary: „Ulrike, Minimusiker & info@…“
export function participantsText(list: Address[], ownAddresses: Set<string>): string {
  const names = list.map((p) =>
    ownAddresses.has(p.address.toLowerCase())
      ? 'Minimusiker'
      : (p.name?.split(/\s+/)[0] ?? p.address),
  );
  const uniq = [...new Set(names)];
  if (uniq.length <= 2) return uniq.join(' & ');
  return uniq.slice(0, -1).join(', ') + ' & ' + uniq[uniq.length - 1];
}

export function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
